import L from '@anlan/gs-map'
import type { FitBoundsOptions, LatLngBoundsExpression, Map as LeafletMap, MapOptions } from '@anlan/gs-map'
import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { LeafletProvider, createLeafletContext } from './core'
import type { LeafletContextInterface } from './core'

export interface MapContainerProps extends MapOptions {
    bounds?: LatLngBoundsExpression
    boundsOptions?: FitBoundsOptions
    children?: ReactNode
    className?: string
    id?: string
    placeholder?: ReactNode
    style?: CSSProperties
    whenReady?: () => void
}

function MapContainerComponent(
    {
        bounds,
        boundsOptions,
        center,
        children,
        className,
        id,
        placeholder,
        style,
        whenReady,
        zoom,
        ...options
    }: MapContainerProps,
    forwardedRef: React.ForwardedRef<LeafletMap | null>
) {
    const [props] = useState({ className, id, style })
    const [context, setContext] = useState<LeafletContextInterface | null>(null)
    const mapInstanceRef = useRef<LeafletMap | null>(null)
    const initialConfigRef = useRef({ bounds, boundsOptions, center, options, whenReady, zoom })

    useImperativeHandle<LeafletMap | null, LeafletMap | null>(forwardedRef, () => context?.map ?? null, [context])

    const mapRef = useCallback((node: HTMLDivElement | null) => {
        if (node === null) {
            mapInstanceRef.current?.remove()
            mapInstanceRef.current = null
            setContext(null)
            return
        }

        if (mapInstanceRef.current !== null) return

        const { bounds, boundsOptions, center, options, whenReady, zoom } = initialConfigRef.current
        const map = new L.GS.map(node, options)
        mapInstanceRef.current = map
        if (center != null && zoom != null) {
            map.setView(center, zoom)
        } else if (bounds != null) {
            map.fitBounds(bounds, boundsOptions)
        }
        if (whenReady != null) {
            map.whenReady(whenReady)
        }
        setContext(createLeafletContext(map))
    }, [])

    const contents = context ? <LeafletProvider value={context}>{children}</LeafletProvider> : placeholder ?? null
    return <div {...props} ref={mapRef}>{contents}</div>
}

export const MapContainer = forwardRef(MapContainerComponent)
